import React, { useState } from 'react';
import { Avatar, Tooltip, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { User } from '../../types';

interface AvatarsListProps {
  items: User[];
  onItemClick?: (userId: string) => void;
}

/** Наведённый подпрыгивает, соседи — тем слабее, чем дальше стоят. */
const LIFT = -4;
const SCALE = 1.05;
const FALLOFF = 0.45;

/**
 * Инлайновые переменные для пружины. Возврат едет по другой кривой: обратно
 * аватары падают с лёгким перелётом, а поднимаются плавно.
 */
function springStyle(index: number, hovered: number | null): React.CSSProperties {
  const isResting = hovered === null;
  return {
    '--avatar-index': index,
    '--shift': isResting ? '0px' : `${(LIFT * FALLOFF ** Math.abs(index - hovered)).toFixed(3)}px`,
    '--scale-active': index === hovered ? SCALE : 1,
    transitionTimingFunction: isResting ? 'var(--avatar-ease-out)' : 'var(--avatar-ease-in)',
  } as React.CSSProperties;
}

export const AvatarsList: React.FC<AvatarsListProps> = ({ items, onItemClick }) => {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Показываем максимум 5 аватаров
  const displayedItems = items.slice(0, 5);
  const remainingCount = items.length - 5;

  return (
    <div className="avatars-list" onMouseLeave={() => setHoveredIndex(null)}>
      {displayedItems.map((item, index) => (
        <Tooltip key={item._id} label={item.name}>
          <Avatar
            src={item.avatar}
            size="sm"
            radius="xl"
            className={`avatars-list__avatar ${index === 0 ? 'avatars-list__avatar--lead' : ''} ${
              !item.avatar ? 'avatars-list__avatar--no-image' : ''
            }`}
            data-index={index}
            style={springStyle(index, hoveredIndex)}
            onMouseEnter={() => setHoveredIndex(index)}
            onClick={() => onItemClick?.(item._id)}
          />
        </Tooltip>
      ))}

      {/* Если участников больше 5, показываем +N */}
      {remainingCount > 0 && (
        <Tooltip label={t('teams.moreMembersTooltip', { count: remainingCount })}>
          <Avatar
            size="sm"
            radius="xl"
            className="avatars-list__avatar avatars-list__avatar--plus"
            style={springStyle(displayedItems.length, hoveredIndex)}
            onMouseEnter={() => setHoveredIndex(displayedItems.length)}
          >
            <Text size="xs" fw={500}>
              +{remainingCount}
            </Text>
          </Avatar>
        </Tooltip>
      )}
    </div>
  );
};
